import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { Alert } from 'react-bootstrap';
import { venueAddressRequired } from '../../Helpers/validators';
import { PlacesSearch } from '@jauntin/react-ui';
import venuePropType from '../../Constants/venueTypes';
import SpecialTaxFields from './SpecialTaxFields';

const SearchByAddress = ({
  onSelect,
  address,
  venueState,
  mobile,
  isValidAddress,
  clearForm,
  hasInput,
  isValidState,
  isValidVenueName,
  hasRegionalTax,
  taxRegions,
  isKentuckyEntity,
  name,
  setIsKentuckyCity,
  setHasLookedUpTax,
  setFormKentuckyCity,
  venueSearchReadOnly,
  setVenueSearchReadOnly,
  isBlocked,
  genericServerError,
  venueName,
  searchCountries,
}) => {
  let errorMessage = '';

  if (venueState) {
    errorMessage = isValidState()
      ? 'A specific venue address is required'
      : 'GatherGuard is not available in Hawaii, Puerto Rico or the Virgin Islands';

    if (isValidState() && !isValidVenueName()) {
      errorMessage = 'A venue name is required';
    }
  } else {
    errorMessage =
      'Not enough venue information available. Please search again or enter a venue code if available.';
  }

  return (
    <>
      <Field
        component={PlacesSearch}
        placeholder={
          mobile ? 'Search for your venue' : 'Search for your venue by name'
        }
        label="Where will the event take place?"
        googleMapsApiKey={process.env.REACT_APP_GOOGLE_API_KEY}
        name={name}
        validate={[venueAddressRequired]}
        hasInput={hasInput}
        readOnly={venueSearchReadOnly}
        showSearchIcon={true}
        onSelect={(
          placeId,
          address,
          addressComponents,
          utcOffset,
          placeName,
        ) => {
          onSelect(
            placeName,
            placeId,
            address,
            addressComponents,
            utcOffset,
            setIsKentuckyCity,
            setHasLookedUpTax,
            setVenueSearchReadOnly,
          );
        }}
        clearForm={() => clearForm(setVenueSearchReadOnly)}
        searchCountries={searchCountries}
      />

      {!isBlocked && (
        <SpecialTaxFields
          hasRegionalTax={hasRegionalTax}
          taxRegions={taxRegions}
          isKentuckyEntity={isKentuckyEntity}
          setFormKentuckyCity={setFormKentuckyCity}
        />
      )}

      {isBlocked && (
        <Alert variant="info" className="mt-3">
          <i className="far fa-exclamation-circle alert__icon" />
          <div className="alert__text">
            Unfortunately, we cannot provide a policy from our online special
            event insurance program for the location provided. For more
            information, please call{' '}
            <span className="text-nowrap">844-747-6240</span>.
          </div>
        </Alert>
      )}

      {genericServerError && (
        <Alert variant="danger" className="mt-3">
          <i className="far fa-exclamation-circle alert__icon" />
          <div className="alert__text">
            Something went wrong. Please try again.
          </div>
        </Alert>
      )}

      {address && !isBlocked && (
        <>
          {isValidState() && isValidAddress() ? (
            <>
              <div className="font-weight-bold">
                Please verify the following address before confirming the venue
              </div>
              <div>
                <i className="fal fa-check-circle text-primary" />
                &nbsp;
                <span className="font-italic">
                  {venueName && `${venueName}, `}
                  {address}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="font-weight-bold text-danger">{errorMessage}</div>
              <div>
                <i className="fal fa-times-circle text-danger" />
                &nbsp;
                <span className="font-italic text-danger">{address}</span>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

SearchByAddress.propTypes = {
  onSelect: PropTypes.func.isRequired,
  address: PropTypes.string.isRequired,
  venueState: PropTypes.string.isRequired,
  mobile: PropTypes.bool.isRequired,
  isValidAddress: PropTypes.func.isRequired,
  clearForm: PropTypes.func.isRequired,
  hasInput: PropTypes.bool.isRequired,
  isValidState: PropTypes.func.isRequired,
  isValidVenueName: PropTypes.func.isRequired,
  hasRegionalTax: PropTypes.bool.isRequired,
  taxRegions: PropTypes.arrayOf(venuePropType).isRequired,
  isKentuckyEntity: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  setIsKentuckyCity: PropTypes.func.isRequired,
  setHasLookedUpTax: PropTypes.func.isRequired,
  setFormKentuckyCity: PropTypes.func.isRequired,
  venueSearchReadOnly: PropTypes.bool.isRequired,
  setVenueSearchReadOnly: PropTypes.func.isRequired,
  isBlocked: PropTypes.bool.isRequired,
  genericServerError: PropTypes.bool.isRequired,
  venueName: PropTypes.string.isRequired,
  searchCountries: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default SearchByAddress;
